import {
  findIndustryCode,
  industryCodeOptions,
  INDUSTRY_CODE_VISIBLE_LIMIT,
  normalizeCatalogueSearch,
  type CustomerIndustry,
} from '@erp/shared';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchCombobox } from '@/components/common/search-combobox';

export function IndustryCodeSelect({
  countryCode,
  value,
  onChange,
  invalid,
}: {
  countryCode: string | null | undefined;
  value: string | null | undefined;
  onChange: (code: string | null, industry?: CustomerIndustry) => void;
  invalid: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const options = useMemo(
    () =>
      industryCodeOptions(countryCode).map((entry) => ({
        id: entry.code,
        label: `${entry.code} · ${entry.name}`,
        search: normalizeCatalogueSearch(`${entry.code} ${entry.name}`),
        compactCode: entry.code.replaceAll('.', '').toLowerCase(),
      })),
    [countryCode],
  );
  const normalizedQuery = normalizeCatalogueSearch(query);
  const filtered = options.filter(
    (entry) =>
      entry.search.includes(normalizedQuery) ||
      entry.compactCode.includes(normalizedQuery.replaceAll('.', '')),
  );
  const selected = options.find((entry) => entry.id === value);
  return (
    <div className="min-w-0 flex-1 space-y-1.5">
      <SearchCombobox
        id="p-industry-code"
        ariaLabel={t('partners.fields.industryCode')}
        ariaInvalid={invalid}
        ariaDescribedBy={
          invalid
            ? 'p-industry-code-description p-industry-code-error'
            : 'p-industry-code-description'
        }
        value={selected?.label ?? value ?? ''}
        placeholder={t('common.pleaseSelect')}
        options={[
          ...(!normalizedQuery ? [{ id: '', label: t('common.none') }] : []),
          ...filtered.slice(0, INDUSTRY_CODE_VISIBLE_LIMIT),
        ]}
        emptyLabel={t('international.noResults')}
        onQueryChange={setQuery}
        onSelect={(option) => {
          const entry = findIndustryCode(countryCode, option.id);
          onChange(option.id || null, entry?.industry);
          setQuery('');
        }}
      />
      {filtered.length > INDUSTRY_CODE_VISIBLE_LIMIT ? (
        <p className="text-xs text-muted-foreground">
          {t('partners.fields.industryCodeSearchHint', { count: filtered.length })}
        </p>
      ) : null}
    </div>
  );
}
